/**
 * Automatic covariate selection (brief 9.14, MET-13), implemented
 * behind the selectCovariates stub in engine.ts.
 *
 * Pipeline, in the brief's order:
 * 1. Honor user exclusions (config, not a data decision — not recorded
 *    in the search path) and locks. A locked candidate is never
 *    dropped by the screens or the search and always appears in the
 *    selected set; it is still subject to the missingness and
 *    constant screens, because no fit can use such a column.
 * 2. Screening: exclude candidates with missing (non-finite) values
 *    among the cell's areas (QC-13) or near-zero variance.
 * 3. Collinearity screen: for each pair with |correlation| above the
 *    threshold, drop the candidate less strongly correlated with
 *    thetahat (a locked candidate is never the one dropped; two
 *    locked candidates are both kept). Then, while any candidate's
 *    variance inflation factor exceeds the VIF threshold, drop the
 *    above-threshold candidate least correlated with thetahat, until
 *    none remains (locked candidates are never dropped, but do take
 *    part as regressors).
 * 4. Size limit: p_max = max(1, min(6, floor(m / 8))) by default
 *    (config.maxCovariates overrides), excluding the intercept.
 *    Locked candidates count toward p_max; a lock set larger than
 *    p_max is returned as-is (the user's explicit choice) with no
 *    further additions.
 * 5. Search over the surviving non-locked candidates: exhaustive
 *    over subsets up to the remaining size limit when 12 or fewer
 *    survive screening, forward stepwise otherwise (additions stop
 *    when no candidate improves the criterion). The criterion is the
 *    ML information criterion of brief 9.7 — BIC by default, AIC on
 *    request — from a genuine ML fit of sigma2_v (Fisher scoring on
 *    the ML score and information), never the REML likelihood, which
 *    is not comparable across fixed effects.
 *
 * The search path records, in order: one step per screening or
 * screen exclusion (variable = the excluded candidate, criterionValue
 * = NaN — no criterion exists for a preprocessing decision; the step
 * also carries the screen's reason, so the run's disclosed selection
 * record can state the rule that dropped each covariate — Tier 3
 * verdict fix 1), then the search steps. For stepwise search each step is one accepted
 * addition (variable = the added candidate, criterionValue = the
 * criterion of the resulting model), preceded by the locked-only base
 * model (variable undefined). For exhaustive search each step is the
 * best subset of one size (variable = the subset's names joined with
 * ' + ', criterionValue = its criterion), preceded by the size-0
 * (locked-only) model. Ties in the criterion break toward the
 * smaller subset, then toward the lexicographically earlier subset
 * in candidate order — the search is fully deterministic.
 *
 * The Auto-mode log transformation of skewed candidates (brief 9.14)
 * is deliberately NOT applied here: it is an automation-layer
 * preprocessing step recorded in the run manifest, and this entry
 * point has no manifest channel; runAnalysis applies it before
 * assembling candidates. See validation/parity/OPEN_QUESTIONS.md.
 */
import { DEFAULT_FAY_HERRIOT_FIT_CONFIG } from './config.js';
import { cholesky, cholInverse, dot, matVec, zeros, } from './linalg.js';
/**
 * Plain words for a covariate drop rule (Tier 3 verdict fix 1): the
 * one rendering of each rule, used by the methods report and the
 * app's results so every surface states the drop the same way.
 */
export function covariateDropRuleLabel(rule, criterion) {
    switch (rule) {
        case 'user-excluded':
            return 'excluded in your covariate settings';
        case 'missing-values':
        case 'missing-in-cell':
            return 'missing values among this cell’s areas';
        case 'no-variation':
            return 'no variation across areas';
        case 'collinearity':
            return 'collinearity screen: correlated with another covariate';
        case 'vif':
            return 'collinearity screen: variance inflation factor too high';
        case 'not-selected':
            return `not chosen by the ${criterion.toUpperCase()} search`;
        case 'size-limit':
            return 'over the cell size limit';
        case 'degrees-of-freedom':
            return 'dropped to leave enough degrees of freedom';
        case 'singular-design':
            return 'the model design became singular with it included';
        case 'selection-unavailable':
            return 'automatic selection could not run for this cell';
    }
}
/** The brief 9.14 size-limit heuristic: p_max = max(1, min(6, floor(m / 8))). */
export function maxCovariatesFor(m, config) {
    if (config.maxCovariates !== undefined)
        return config.maxCovariates;
    return Math.max(1, Math.min(6, Math.floor(m / 8)));
}
function meanOf(values) {
    return values.reduce((acc, v) => acc + v, 0) / values.length;
}
function sampleVariance(values) {
    if (values.length < 2)
        return 0;
    const mean = meanOf(values);
    return (values.reduce((acc, v) => acc + (v - mean) * (v - mean), 0) /
        (values.length - 1));
}
function pearson(a, b) {
    const ma = meanOf(a);
    const mb = meanOf(b);
    let sab = 0;
    let saa = 0;
    let sbb = 0;
    for (let i = 0; i < a.length; i++) {
        const da = a[i] - ma;
        const db = b[i] - mb;
        sab += da * db;
        saa += da * da;
        sbb += db * db;
    }
    if (saa <= 0 || sbb <= 0)
        return 0;
    return sab / Math.sqrt(saa * sbb);
}
/**
 * VIF of column j regressed on the other columns (with intercept).
 * The regression runs on a maximal independent subset of the other
 * columns, found by pivoted elimination on the normal equations:
 * when the other columns are collinear among themselves, the fitted
 * values (the projection onto their span) are still well defined,
 * so an uninvolved column is not assigned an infinite VIF merely
 * because its regressors are redundant.
 */
function varianceInflationFactor(columns, j) {
    const m = columns[j].length;
    const others = columns.filter((_, k) => k !== j);
    const target = columns[j];
    const meanT = meanOf(target);
    const tc = target.map((v) => v - meanT);
    let sst = 0;
    for (const v of tc)
        sst += v * v;
    if (sst <= 0)
        return Number.POSITIVE_INFINITY;
    if (others.length === 0)
        return 1;
    const centered = others.map((col) => {
        const mu = meanOf(col);
        return col.map((v) => v - mu);
    });
    const p = centered.length;
    // Augmented normal equations [G | g], G = C'C, g = C'tc.
    const A = [];
    let maxDiag = 0;
    for (let a = 0; a < p; a++) {
        const row = new Array(p + 1).fill(0);
        for (let c = 0; c < p; c++) {
            let s = 0;
            for (let i = 0; i < m; i++)
                s += centered[a][i] * centered[c][i];
            row[c] = s;
        }
        let g = 0;
        for (let i = 0; i < m; i++)
            g += centered[a][i] * tc[i];
        row[p] = g;
        if (Math.abs(row[a]) > maxDiag)
            maxDiag = Math.abs(row[a]);
        A.push(row);
    }
    const pivotTol = 1e-10 * Math.max(1, maxDiag);
    const kept = [];
    let rank = 0;
    for (let col = 0; col < p && rank < p; col++) {
        let piv = -1;
        let best = pivotTol;
        for (let r = rank; r < p; r++) {
            if (Math.abs(A[r][col]) > best) {
                best = Math.abs(A[r][col]);
                piv = r;
            }
        }
        if (piv === -1)
            continue; // dependent regressor: skip it
        [A[rank], A[piv]] = [A[piv], A[rank]];
        const pivotValue = A[rank][col];
        for (let r = rank + 1; r < p; r++) {
            const factor = A[r][col] / pivotValue;
            if (factor === 0)
                continue;
            for (let c = col; c <= p; c++)
                A[r][c] -= factor * A[rank][c];
        }
        kept.push(col);
        rank++;
    }
    // Back-substitution over kept columns (their rows are the first
    // `rank` rows, in kept order).
    const coef = new Array(p).fill(0);
    for (let k = rank - 1; k >= 0; k--) {
        const col = kept[k];
        let s = A[k][p];
        for (let q = k + 1; q < rank; q++)
            s -= A[k][kept[q]] * coef[kept[q]];
        coef[col] = s / A[k][col];
    }
    let sse = 0;
    for (let i = 0; i < m; i++) {
        let fitted = 0;
        for (let a = 0; a < p; a++)
            fitted += centered[a][i] * coef[a];
        const resid = tc[i] - fitted;
        sse += resid * resid;
    }
    const r2 = 1 - sse / sst;
    if (r2 >= 1)
        return Number.POSITIVE_INFINITY;
    return 1 / (1 - r2);
}
/**
 * ML fit of sigma2_v for one design (brief 9.7): Fisher scoring on
 * the ML score and information from the Prasad-Rao starting value,
 * boundary at sigma2_v = 0. Returns null when the weighted design is
 * singular (the subset is then scored +Infinity by the search).
 * The ML log-likelihood is invariant to full-rank reparameterization
 * of the columns, so fits run on the raw (unstandardized) design.
 */
function mlFit(y, psi, X, criterion) {
    const m = y.length;
    const p = X[0].length;
    const tol = DEFAULT_FAY_HERRIOT_FIT_CONFIG.convergenceTolerance;
    const maxIt = DEFAULT_FAY_HERRIOT_FIT_CONFIG.maxIterations;
    const core = (s) => {
        const M = zeros(p, p);
        const rhs = new Array(p).fill(0);
        let sumQ = 0;
        let sumQ2 = 0;
        for (let i = 0; i < m; i++) {
            const q = 1 / (s + psi[i]);
            sumQ += q;
            sumQ2 += q * q;
            const xi = X[i];
            for (let a = 0; a < p; a++) {
                rhs[a] += q * xi[a] * y[i];
                for (let b = 0; b < p; b++)
                    M[a][b] += q * xi[a] * xi[b];
            }
        }
        const L = cholesky(M);
        if (L === null)
            return null;
        const beta = matVec(cholInverse(L), rhs);
        let sumQr2 = 0;
        let sumQ2r2 = 0;
        let sumLog = 0;
        for (let i = 0; i < m; i++) {
            const r = y[i] - dot(X[i], beta);
            const q = 1 / (s + psi[i]);
            sumQr2 += q * r * r;
            sumQ2r2 += q * q * r * r;
            sumLog += Math.log(s + psi[i]);
        }
        return {
            beta,
            // ML score (brief 9.7): -0.5 sum q + 0.5 sum q^2 r^2, with
            // expected information 0.5 sum q^2.
            score: -0.5 * sumQ + 0.5 * sumQ2r2,
            info: 0.5 * sumQ2,
            logLik: -0.5 * (m * Math.log(2 * Math.PI) + sumLog + sumQr2),
        };
    };
    // Prasad-Rao starting value (brief 9.7), OLS-based.
    let s0 = 0;
    {
        const XtX = zeros(p, p);
        const Xty = new Array(p).fill(0);
        for (let i = 0; i < m; i++) {
            for (let a = 0; a < p; a++) {
                Xty[a] += X[i][a] * y[i];
                for (let b = 0; b < p; b++)
                    XtX[a][b] += X[i][a] * X[i][b];
            }
        }
        const L = cholesky(XtX);
        if (L === null)
            return null;
        const inv = cholInverse(L);
        const betaOls = matVec(inv, Xty);
        let sumU2 = 0;
        let sumPsiH = 0;
        for (let i = 0; i < m; i++) {
            const u = y[i] - dot(X[i], betaOls);
            const h = dot(X[i], matVec(inv, X[i]));
            sumU2 += u * u;
            sumPsiH += psi[i] * (1 - h);
        }
        s0 = Math.max(0, (sumU2 - sumPsiH) / (m - p));
    }
    const sortedPsi = [...psi].sort((a, b) => a - b);
    const medianPsi = sortedPsi[Math.floor(sortedPsi.length / 2)];
    let s = Math.max(s0, 0.1 * medianPsi);
    let current = core(s);
    if (current === null)
        return null;
    for (let it = 0; it < maxIt; it++) {
        if (!Number.isFinite(current.score) || !Number.isFinite(current.info) || current.info <= 0)
            break;
        let sNew = s + current.score / current.info;
        if (!Number.isFinite(sNew))
            break;
        if (sNew <= 0) {
            // ML boundary: the maximizer is at (or arbitrarily near) 0.
            s = 0;
            current = core(0);
            if (current === null)
                return null;
            break;
        }
        const next = core(sNew);
        if (next === null)
            return null;
        const done = Math.abs(sNew - s) < tol * (1 + sNew);
        s = sNew;
        current = next;
        if (done)
            break;
    }
    const logLik = current.logLik;
    const crit = criterion === 'bic'
        ? -2 * logLik + (p + 1) * Math.log(m)
        : -2 * logLik + 2 * (p + 1);
    return { logLik, criterion: crit };
}
export function selectCovariatesImpl(input) {
    const { thetaHat, psi, candidates, config } = input;
    const m = thetaHat.length;
    if (psi.length !== m) {
        throw new Error(`selectCovariates: thetaHat has ${m} areas but psi has ${psi.length}.`);
    }
    for (let i = 0; i < m; i++) {
        if (!Number.isFinite(thetaHat[i])) {
            throw new Error(`selectCovariates: thetaHat[${i}] is not finite.`);
        }
        if (!Number.isFinite(psi[i]) || psi[i] <= 0) {
            throw new Error(`selectCovariates: psi[${i}] must be positive and finite; received ${psi[i]}.`);
        }
    }
    const lockedSet = new Set(config.locked);
    const excludedSet = new Set(config.excluded);
    const names = Object.keys(candidates).filter((n) => !excludedSet.has(n));
    for (const n of names) {
        if (candidates[n].length !== m) {
            throw new Error(`selectCovariates: candidate "${n}" has ${candidates[n].length} values for ${m} areas.`);
        }
    }
    const path = [];
    const recordScreen = (variable, reason) => {
        path.push({ step: path.length, variable, criterionValue: Number.NaN, reason });
    };
    // --- Screening: missingness and near-zero variance (brief 9.14). --
    const survivors = [];
    for (const name of names) {
        const values = candidates[name];
        const missing = values.some((v) => !Number.isFinite(v));
        const sd = missing ? 0 : Math.sqrt(sampleVariance(values));
        const nearZero = !missing && sd <= 1e-12 * Math.max(1, Math.abs(meanOf(values)));
        if (missing) {
            recordScreen(name, 'missing-values');
            continue;
        }
        if (nearZero) {
            recordScreen(name, 'no-variation');
            continue;
        }
        survivors.push(name);
    }
    // --- Collinearity screen, pairs (brief 9.14). ---------------------
    const corrWithY = new Map();
    for (const name of survivors) {
        corrWithY.set(name, Math.abs(pearson(candidates[name], thetaHat)));
    }
    const alive = [...survivors];
    let pairDropped = true;
    while (pairDropped) {
        pairDropped = false;
        outer: for (let a = 0; a < alive.length; a++) {
            for (let b = a + 1; b < alive.length; b++) {
                const na = alive[a];
                const nb = alive[b];
                const c = Math.abs(pearson(candidates[na], candidates[nb]));
                if (c <= config.correlationThreshold)
                    continue;
                const la = lockedSet.has(na);
                const lb = lockedSet.has(nb);
                if (la && lb)
                    continue;
                let drop;
                if (la)
                    drop = nb;
                else if (lb)
                    drop = na;
                else {
                    const ca = corrWithY.get(na) ?? 0;
                    const cb = corrWithY.get(nb) ?? 0;
                    drop = ca < cb ? na : nb; // tie: drop the later candidate
                }
                alive.splice(alive.indexOf(drop), 1);
                recordScreen(drop, 'collinearity');
                pairDropped = true;
                break outer;
            }
        }
    }
    // --- Collinearity screen, VIF (brief 9.14). -----------------------
    // While any non-locked candidate's VIF exceeds the threshold, drop
    // the above-threshold candidate least correlated with thetahat
    // (ties: the later candidate) — the same sacrifice rule as the
    // pairwise screen, so an exact linear dependence among several
    // candidates costs the least outcome-relevant one, not whichever
    // happens to be enumerated first.
    for (;;) {
        const columns = alive.map((n) => candidates[n]);
        let dropName;
        let dropCorrY = Number.POSITIVE_INFINITY;
        for (let j = 0; j < alive.length; j++) {
            if (lockedSet.has(alive[j]))
                continue;
            const vif = varianceInflationFactor(columns, j);
            if (!(vif > config.vifThreshold))
                continue;
            const cy = corrWithY.get(alive[j]) ?? 0;
            if (cy <= dropCorrY) {
                dropCorrY = cy;
                dropName = alive[j];
            }
        }
        if (dropName === undefined)
            break;
        alive.splice(alive.indexOf(dropName), 1);
        recordScreen(dropName, 'vif');
    }
    const lockedAlive = alive.filter((n) => lockedSet.has(n));
    const pool = alive.filter((n) => !lockedSet.has(n));
    const pMax = maxCovariatesFor(m, config);
    const slots = Math.max(0, pMax - lockedAlive.length);
    const designFor = (subset) => {
        const cols = [...lockedAlive, ...subset];
        return thetaHat.map((_, i) => [
            1,
            ...cols.map((n) => candidates[n][i]),
        ]);
    };
    const criterionOf = (subset) => {
        const p = 1 + lockedAlive.length + subset.length;
        if (m - p < 1)
            return Number.POSITIVE_INFINITY;
        const fit = mlFit(thetaHat, psi, designFor(subset), config.criterion);
        return fit === null ? Number.POSITIVE_INFINITY : fit.criterion;
    };
    const baseCriterion = criterionOf([]);
    path.push({ step: path.length, criterionValue: baseCriterion });
    let chosen = [];
    if (slots > 0 && pool.length > 0) {
        if (pool.length <= 12) {
            // Exhaustive over subsets up to the size limit (brief 9.14).
            const maxSize = Math.min(slots, pool.length);
            let bestOverall = {
                subset: [],
                criterion: baseCriterion,
            };
            for (let size = 1; size <= maxSize; size++) {
                let bestOfSize;
                const idx = Array.from({ length: size }, (_, i) => i);
                for (;;) {
                    const subset = idx.map((i) => pool[i]);
                    const crit = criterionOf(subset);
                    if (bestOfSize === undefined ||
                        crit < bestOfSize.criterion) {
                        bestOfSize = { subset, criterion: crit };
                    }
                    // Next combination in lexicographic index order.
                    let k = size - 1;
                    while (k >= 0 && idx[k] === pool.length - size + k)
                        k--;
                    if (k < 0)
                        break;
                    idx[k]++;
                    for (let j = k + 1; j < size; j++)
                        idx[j] = idx[j - 1] + 1;
                }
                if (bestOfSize !== undefined) {
                    path.push({
                        step: path.length,
                        variable: bestOfSize.subset.join(' + '),
                        criterionValue: bestOfSize.criterion,
                    });
                    if (bestOfSize.criterion < bestOverall.criterion) {
                        bestOverall = bestOfSize;
                    }
                }
            }
            chosen = bestOverall.subset;
        }
        else {
            // Forward stepwise (brief 9.14): add the best candidate while
            // the criterion improves and slots remain.
            const remaining = [...pool];
            let currentCriterion = baseCriterion;
            while (chosen.length < slots && remaining.length > 0) {
                let bestName;
                let bestCriterion = Number.POSITIVE_INFINITY;
                for (const name of remaining) {
                    const crit = criterionOf([...chosen, name]);
                    if (crit < bestCriterion) {
                        bestCriterion = crit;
                        bestName = name;
                    }
                }
                if (bestName === undefined || !(bestCriterion < currentCriterion))
                    break;
                chosen.push(bestName);
                remaining.splice(remaining.indexOf(bestName), 1);
                currentCriterion = bestCriterion;
                path.push({
                    step: path.length,
                    variable: bestName,
                    criterionValue: bestCriterion,
                });
            }
        }
    }
    return {
        selected: [...lockedAlive, ...chosen],
        criterion: config.criterion,
        path,
    };
}
//# sourceMappingURL=selection.js.map